import { type ColDef, type ColumnState, type GridState } from "ag-grid-community"

import {
  completeColumnState,
  spliceAfterNearestPredecessor,
} from "~/components/planning/ProductTable/utils/columnPlacement"
import { liveColumns } from "~/components/planning/ProductTable/utils/columnVisibility"
import {
  getGridApiById,
  type SavedStateColumn,
  type SavedStateMerge,
  updatePersistedState,
} from "~/hooks/useGridState"

/**
 * A table's own visibility rule: `true` or `false` forces a column shown or hidden whatever the
 * user or a view chose, `undefined` leaves it to them. Such columns stay out of the column chooser.
 */
export type ForcedVisibility = (colId: string) => boolean | undefined

/**
 * Chooser entries that stand for several grid columns, by entry id. The entry is shown while any
 * member is, and members no forced rule hides follow it, so a rule change that moves which members
 * may show keeps the entry's state.
 */
export type ColumnGroups = Readonly<Record<string, readonly string[]>>

export const NO_GROUPS: ColumnGroups = {}

const groupIdsByMember = (groups: ColumnGroups) =>
  new Map(
    Object.entries(groups).flatMap(([groupId, members]) =>
      members.map((m) => [m, groupId] as const),
    ),
  )

/**
 * `forced` plus the group rule: a member `forced` leaves free shows while any member of its group
 * `isShown` says is shown. Read against the state before a patch, so a group survives its shown
 * members being forced hidden.
 */
export function groupedVisibility(
  forced: ForcedVisibility,
  groups: ColumnGroups,
  isShown: (colId: string) => boolean,
): ForcedVisibility {
  const groupOf = groupIdsByMember(groups)
  if (groupOf.size === 0) return forced
  return (colId) => {
    const own = forced(colId)
    const groupId = groupOf.get(colId)
    return own !== undefined || groupId === undefined ? own : (groups[groupId] ?? []).some(isShown)
  }
}

const isRowGroupColumn = (colDef: ColDef) => !!colDef.rowGroup || colDef.rowGroupIndex != null

/**
 * The colDefs a grid-owned table registers, with no live `hide`: a defined `hide` is re-applied on
 * every columnDefs update, which would override the grid. A colDef with `hide: true` has no data to
 * show and is left out until it has. Row-group columns keep their `hide`. A column starts as
 * `forced` says, else shown when it is locked visible or a default; a group member, when a member
 * of its group is a default (`defaultColumnIds` holds members, not group entries).
 */
export function gridOwnedColDefs<T>(
  colDefs: readonly ColDef<T>[],
  defaultColumnIds: ReadonlySet<string>,
  ownForced: ForcedVisibility,
  groups: ColumnGroups,
): ColDef<T>[] {
  const forced = groupedVisibility(ownForced, groups, (colId) => defaultColumnIds.has(colId))
  return colDefs.flatMap((colDef) => {
    if (isRowGroupColumn(colDef)) return [colDef]
    if (colDef.hide === true) return []
    const colId = colDef.colId ?? ""
    const shows = forced(colId) ?? (!!colDef.lockVisible || defaultColumnIds.has(colId))
    return [{ ...colDef, hide: undefined, initialHide: !shows }]
  })
}

/** Whether the column chooser lists the column: locked, row-group and forced columns aren't the user's. */
export const isChooserColumn = (colDef: ColDef, forced: ForcedVisibility) =>
  !!colDef.colId &&
  !colDef.lockVisible &&
  !isRowGroupColumn(colDef) &&
  forced(colDef.colId) === undefined

/**
 * The chooser's entries in column order: the {@link isChooserColumn} columns, with each group's
 * members replaced by its entry where the first of them sits. A group with no free member has none.
 */
export function chooserIdsOf(
  colDefs: readonly ColDef[],
  forced: ForcedVisibility,
  groups: ColumnGroups,
): string[] {
  const groupOf = groupIdsByMember(groups)
  const ids = new Set<string>()
  for (const colDef of colDefs) {
    if (!isChooserColumn(colDef, forced)) continue
    const colId = colDef.colId ?? ""
    ids.add(groupOf.get(colId) ?? colId)
  }
  return [...ids]
}

/** The chooser entries `isShown` reports shown, a group's entry while any of its members is. */
export function shownChooserIds(
  chooserIds: readonly string[],
  groups: ColumnGroups,
  isShown: (colId: string) => boolean,
): string[] {
  return chooserIds.filter((id) => (groups[id] ? groups[id].some(isShown) : isShown(id)))
}

/** `ids` with each group entry replaced by its members. */
export const expandGroups = (ids: readonly string[], groups: ColumnGroups) =>
  ids.flatMap((id) => groups[id] ?? [id])

/**
 * Column state showing the columns `shows` accepts and hiding the ones it rejects, where they
 * differ. Columns it returns `undefined` for are left alone.
 */
export function visibilityPatchBy(
  columns: readonly { colId: string; visible: boolean }[],
  shows: (colId: string) => boolean | undefined,
): ColumnState[] {
  return columns.flatMap(({ colId, visible }) => {
    const show = shows(colId)
    return show === undefined || show === visible ? [] : [{ colId, hide: !show }]
  })
}

/**
 * A stored or view state completed against the registered columns (see
 * {@link completeColumnState}), with locked-visible columns shown and `forced` and the group rule
 * applied, so no state can override the table's own rules.
 */
export function migrateGridOwnedState(
  savedState: GridState,
  columns: readonly SavedStateColumn[],
  selectedColIds: ReadonlySet<string>,
  ownForced: ForcedVisibility,
  groups: ColumnGroups,
): GridState {
  const state = completeColumnState(savedState, columns, selectedColIds)
  const hidden = new Set(state.columnVisibility?.hiddenColIds)
  const storedHidden = new Set(hidden)
  const forced = groupedVisibility(ownForced, groups, (colId) => !storedHidden.has(colId))
  for (const { colId, lockVisible } of columns) {
    const shows = lockVisible ? true : forced(colId)
    if (shows === true) hidden.delete(colId)
    else if (shows === false) hidden.add(colId)
  }
  return { ...state, columnVisibility: { hiddenColIds: [...hidden] } }
}

/** A grid-owned table's own rules, which a chooser commit must not override. */
export type GridOwnedRules = { forced: ForcedVisibility; groups: ColumnGroups }

const FREE_RULES: GridOwnedRules = { forced: () => undefined, groups: NO_GROUPS }
const rulesByTable = new Map<string, GridOwnedRules>()

/** Registers the rules {@link commitShownColumns} applies on the grid `tableId`; returns the unregister. */
export function registerGridOwnedRules(tableId: string, rules: GridOwnedRules) {
  rulesByTable.set(tableId, rules)
  return () => {
    if (rulesByTable.get(tableId) === rules) rulesByTable.delete(tableId)
  }
}

/** The rules registered for the grid `tableId`; free rules while none is. */
export const gridOwnedRulesOf = (tableId: string): GridOwnedRules =>
  rulesByTable.get(tableId) ?? FREE_RULES

/**
 * Column state showing the chooser entries in `shownIds` and hiding the other `chooserIds`, a group
 * entry standing for its members. Members `rules.forced` governs keep its say.
 */
export function shownColumnsPatch(
  columns: readonly { colId: string; visible: boolean }[],
  chooserIds: readonly string[],
  shownIds: readonly string[],
  { forced, groups }: GridOwnedRules,
): ColumnState[] {
  const chooser = new Set(expandGroups(chooserIds, groups))
  const shown = new Set(expandGroups(shownIds, groups))
  return visibilityPatchBy(columns, (colId) =>
    chooser.has(colId) ? (forced(colId) ?? shown.has(colId)) : undefined,
  )
}

/** {@link shownColumnsPatch} applied to the grid `tableId` under its registered rules. */
export function commitShownColumns(
  tableId: string,
  chooserIds: readonly string[],
  shownIds: readonly string[],
) {
  const api = getGridApiById(tableId)
  if (!api || api.isDestroyed()) return
  const state = shownColumnsPatch(liveColumns(api), chooserIds, shownIds, gridOwnedRulesOf(tableId))
  if (state.length > 0) api.applyColumnState({ state })
}

/**
 * `next` keeping what `previous` saved for the columns the grid doesn't have right now (no data in
 * this view of a shared table): their slot, visibility and width. A save otherwise drops them, and
 * the table forgets the user's choice for every view that does have them.
 */
export const keepAbsentColumns: SavedStateMerge = (next, previous) => {
  const previousOrder = previous?.columnOrder?.orderedColIds
  const nextOrder = next.columnOrder?.orderedColIds
  if (!previousOrder || !nextOrder) return next
  const present = new Set(nextOrder)
  const absent = previousOrder.filter((colId) => !present.has(colId))
  if (absent.length === 0) return next
  const isAbsent = (colId: string) => !present.has(colId)
  const sizing = [
    ...(next.columnSizing?.columnSizingModel ?? []),
    ...(previous.columnSizing?.columnSizingModel ?? []).filter((c) => isAbsent(c.colId)),
  ]
  return {
    ...next,
    columnOrder: {
      orderedColIds: spliceAfterNearestPredecessor(
        previousOrder,
        [...nextOrder, ...absent],
        absent,
      ),
    },
    columnVisibility: {
      hiddenColIds: [
        ...(next.columnVisibility?.hiddenColIds ?? []),
        ...(previous.columnVisibility?.hiddenColIds ?? []).filter(isAbsent),
      ],
    },
    ...(sizing.length > 0 && { columnSizing: { columnSizingModel: sizing } }),
  }
}

/** `state` with each column it lists that isn't in `liveColIds` shown only if in `defaultColumnIds`. */
export function resetAbsentColumns(
  state: GridState,
  liveColIds: ReadonlySet<string>,
  defaultColumnIds: ReadonlySet<string>,
): GridState {
  const absent = (state.columnOrder?.orderedColIds ?? []).filter((colId) => !liveColIds.has(colId))
  if (absent.length === 0) return state
  const hidden = new Set(state.columnVisibility?.hiddenColIds)
  for (const colId of absent) {
    if (defaultColumnIds.has(colId)) hidden.delete(colId)
    else hidden.add(colId)
  }
  return { ...state, columnVisibility: { hiddenColIds: [...hidden] } }
}

/**
 * Resets the choices {@link keepAbsentColumns} carries for the columns the grid `tableId` doesn't
 * have right now, so a chooser reset reaches every column, not only the ones on screen.
 */
export function resetAbsentChoices(
  tableId: string,
  defaultColumnIds: readonly string[],
  groups: ColumnGroups,
) {
  const api = getGridApiById(tableId)
  if (!api || api.isDestroyed()) return
  const live = new Set(liveColumns(api).map((c) => c.colId))
  const defaults = new Set(expandGroups(defaultColumnIds, groups))
  updatePersistedState(tableId, (state) => resetAbsentColumns(state, live, defaults))
}
